import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import endpoints from "../../helpers/endpoints";
import fetch from "../../utils/fetch";
import { getAdminHeaders } from "../../utils/adminAuth";
import styles from "./UrlScanReview.module.scss";

/** Landing công khai khi admin nhấp Dynamic ID */
const PUBLIC_QR_CODE_BASE_URL = (
  typeof process.env.REACT_APP_PUBLIC_QR_CODE_URL === "string" && process.env.REACT_APP_PUBLIC_QR_CODE_URL.trim()
    ? process.env.REACT_APP_PUBLIC_QR_CODE_URL.trim().replace(/\/$/, "")
    : "https://code.nineqr.com"
);

const STATUS_OPTIONS = [
  { value: "reported", label: "Bị báo cáo" },
  { value: "suspicious", label: "Nghi ngờ" },
  { value: "blocked", label: "Đã chặn" },
  { value: "approved", label: "Đã duyệt an toàn" },
  { value: "pending", label: "Chờ quét" },
  { value: "clean", label: "Sạch" },
  { value: "unscanned", label: "Chưa quét" },
  { value: "deleted", label: "Đã xóa" },
  { value: "all", label: "Tất cả" },
];

const STATUS_LABELS: Record<string, string> = {
  reported: "Bị báo cáo",
  suspicious: "Nghi ngờ",
  blocked: "Đã chặn",
  approved: "Đã duyệt",
  pending: "Chờ quét",
  clean: "Sạch",
  unscanned: "Chưa quét",
  deleted: "Đã xóa",
};

const REPORT_REASON_LABELS: Record<string, string> = {
  phishing: "Giả mạo / lừa đảo",
  scam: "Lừa đảo tiền / tài sản",
  malware: "Mã độc / virus",
  inappropriate: "Nội dung không phù hợp",
  spam: "Spam / quảng cáo rác",
  other: "Vấn đề khác",
};

type UrlScanInfo = {
  score?: number;
  reasons?: string[];
  scannedUrl?: string | null;
  scannedAt?: string;
  engine?: string;
  trigger?: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
};

type ReportReason = {
  reason: string;
  note?: string;
  at?: string;
};

type ReportInfo = {
  count?: number;
  firstReportedAt?: string;
  lastReportedAt?: string;
  reasons?: ReportReason[];
};

type CodeRow = {
  id: string;
  dynamicId?: string | null;
  userId?: string;
  workPageId?: string | null;
  ownershipStatus?: string;
  contentType?: string;
  title?: string;
  productId?: string;
  scanCounter?: number;
  enabled?: boolean;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  urlScanStatus?: string;
  urlScan?: UrlScanInfo;
  report?: ReportInfo;
  codeRedirect?: string;
};

type CodeDetail = CodeRow & {
  codeContent?: string;
  ownerEmail?: string | null;
  destinationUrl?: string | null;
};

type Summary = {
  counts: Record<string, number>;
  recentSuspicious7d: number;
};

const KPI_KEYS = ["reported", "suspicious", "blocked", "approved", "clean", "deleted"];

type ChainHop = {
  url: string;
  status?: number;
  contentType?: string | null;
  metaRefresh?: string;
  error?: string;
  analysis?: { verdict: string; score: number; reasons: string[] } | null;
};

type ChainResult = {
  startUrl: string;
  hops: number;
  chain: ChainHop[];
  finalUrl: string;
  finalAnalysis?: { verdict: string; score: number; reasons: string[] } | null;
};

function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat("vi-VN").format(value || 0);
}

function formatDateTime(value: string | undefined | null) {
  if (!value) return "-";
  return new Date(value).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusBadgeClass(status: string | undefined) {
  switch (status) {
    case "suspicious": return `${styles.StatusBadge} ${styles.StatusSuspicious}`;
    case "blocked": return `${styles.StatusBadge} ${styles.StatusBlocked}`;
    case "approved": return `${styles.StatusBadge} ${styles.StatusApproved}`;
    case "clean": return `${styles.StatusBadge} ${styles.StatusClean}`;
    case "pending": return `${styles.StatusBadge} ${styles.StatusPending}`;
    default: return `${styles.StatusBadge} ${styles.StatusUnscanned}`;
  }
}

function scoreClass(score: number | undefined) {
  const s = score || 0;
  if (s >= 60) return styles.ScoreHigh;
  if (s >= 40) return styles.ScoreMedium;
  return styles.ScoreLow;
}

function publicQrCodeUrl(dynamicId: string): string {
  return `${PUBLIC_QR_CODE_BASE_URL}/${encodeURIComponent(dynamicId)}`;
}

const UrlScanReview = () => {
  const [status, setStatus] = useState("suspicious");
  const [q, setQ] = useState("");
  const [minScore, setMinScore] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<CodeRow[]>([]);
  const [meta, setMeta] = useState<{ total: number; pages: number }>({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState("");

  const [detail, setDetail] = useState<CodeDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [chain, setChain] = useState<ChainResult | null>(null);
  const [chainLoading, setChainLoading] = useState(false);

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const allSelected = rows.length > 0 && rows.every((r) => prev.has(r.id));
      return allSelected ? new Set() : new Set(rows.map((r) => r.id));
    });
  }

  const query = useMemo(() => {
    const params: any = { status, page, limit };
    if (q.trim()) params.q = q.trim();
    if (minScore.trim()) params.minScore = minScore.trim();
    return params;
  }, [status, q, minScore, page, limit]);

  async function loadSummary() {
    try {
      const res: any = await fetch(endpoints.URL_SCAN_SUMMARY, { headers: getAdminHeaders() });
      if (res?.status < 400) setSummary(res.body?.result || null);
    } catch (_err) {
      // summary lỗi không chặn bảng chính
    }
  }

  async function loadData(nextPage?: number) {
    setLoading(true);
    setError("");
    setSelectedIds(new Set());
    try {
      const params = { ...query, page: nextPage || page };
      const res: any = await fetch(endpoints.URL_SCAN_CODES, { query: params, headers: getAdminHeaders() });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không tải được danh sách QR.");
      }
      setRows(res.body?.result || []);
      setMeta({
        total: res.body?.meta?.total || 0,
        pages: res.body?.meta?.pages || 1,
      });
    } catch (err: any) {
      setError(err?.message || "Không tải được danh sách QR.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function runBulk(action: "approve" | "block" | "unblock" | "flag" | "delete" | "restore") {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const actionLabels: Record<string, string> = {
      approve: "duyệt an toàn",
      block: "CHẶN",
      unblock: "gỡ chặn",
      flag: "gắn cờ nghi ngờ",
      delete: "XÓA",
      restore: "khôi phục",
    };
    if (action === "delete" || action === "block") {
      const verb = action === "delete" ? "xóa" : "chặn";
      if (!window.confirm(`Bạn chắc chắn muốn ${verb} ${ids.length} QR đã chọn?${action === "delete" ? "\nQR sẽ ngừng hoạt động (chuyển hướng)." : "\nNgười quét sẽ thấy trang cảnh báo thay vì URL đích."}`)) {
        return;
      }
    }
    setBulkLoading(action);
    setError("");
    setMessage("");
    try {
      const res: any = await fetch(endpoints.URL_SCAN_BULK, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({ ids, action, note: reviewNote.trim() || undefined }),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không thực hiện được thao tác hàng loạt.");
      }
      const affected = res.body?.result?.affected ?? ids.length;
      setMessage(`Đã ${actionLabels[action]} ${affected} QR.`);
      setSelectedIds(new Set());
      loadData();
      loadSummary();
    } catch (err: any) {
      setError(err?.message || "Không thực hiện được thao tác hàng loạt.");
    } finally {
      setBulkLoading("");
    }
  }

  async function openDetail(row: CodeRow) {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setChain(null);
    setReviewNote("");
    try {
      const res: any = await fetch(`${endpoints.URL_SCAN_CODES}/${encodeURIComponent(row.id)}`, {
        headers: getAdminHeaders(),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không tải được chi tiết QR.");
      }
      setDetail(res.body?.result || null);
    } catch (err: any) {
      setDetail(null);
      setDetailError(err?.message || "Không tải được chi tiết QR.");
    } finally {
      setDetailLoading(false);
    }
  }

  async function rescan() {
    if (!detail) return;
    setActionLoading("rescan");
    setDetailError("");
    try {
      const res: any = await fetch(`${endpoints.URL_SCAN_CODES}/${encodeURIComponent(detail.id)}/rescan`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({}),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Quét lại thất bại.");
      }
      const result = res.body?.result;
      setDetail({ ...detail, urlScanStatus: result?.urlScanStatus, urlScan: result?.urlScan });
      setMessage(`Đã quét lại ${detail.dynamicId || detail.id}: ${STATUS_LABELS[result?.urlScanStatus] || result?.urlScanStatus}.`);
      loadData();
      loadSummary();
    } catch (err: any) {
      setDetailError(err?.message || "Quét lại thất bại.");
    } finally {
      setActionLoading("");
    }
  }

  async function checkChain() {
    if (!detail) return;
    setChainLoading(true);
    setDetailError("");
    setChain(null);
    try {
      const res: any = await fetch(`${endpoints.URL_SCAN_CODES}/${encodeURIComponent(detail.id)}/check`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({}),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Kiểm tra chuỗi redirect thất bại.");
      }
      setChain(res.body?.result || null);
    } catch (err: any) {
      setDetailError(err?.message || "Kiểm tra chuỗi redirect thất bại.");
    } finally {
      setChainLoading(false);
    }
  }

  async function review(action: "approve" | "block" | "unblock" | "flag") {
    if (!detail) return;
    if (action === "block") {
      const confirmed = window.confirm(
        `Chặn QR ${detail.dynamicId || detail.id}?\nNgười quét sẽ thấy trang cảnh báo thay vì được chuyển tới URL đích.`
      );
      if (!confirmed) return;
    }
    setActionLoading(action);
    setDetailError("");
    try {
      const res: any = await fetch(`${endpoints.URL_SCAN_CODES}/${encodeURIComponent(detail.id)}/review`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({ action, note: reviewNote.trim() || undefined }),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không thực hiện được thao tác.");
      }
      const nextStatus = res.body?.result?.urlScanStatus;
      setDetail({ ...detail, urlScanStatus: nextStatus });
      const actionLabels: Record<string, string> = {
        approve: "duyệt an toàn",
        block: "CHẶN",
        unblock: "mở chặn",
        flag: "gắn cờ nghi ngờ",
      };
      setMessage(`Đã ${actionLabels[action]} QR ${detail.dynamicId || detail.id}.`);
      loadData();
      loadSummary();
    } catch (err: any) {
      setDetailError(err?.message || "Không thực hiện được thao tác.");
    } finally {
      setActionLoading("");
    }
  }

  async function deleteDetail() {
    if (!detail) return;
    if (!window.confirm(`Xóa QR ${detail.dynamicId || detail.id}?\nQR sẽ ngừng hoạt động (chuyển hướng).`)) return;
    setActionLoading("delete");
    setDetailError("");
    try {
      const res: any = await fetch(endpoints.URL_SCAN_BULK, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({ ids: [detail.id], action: "delete" }),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không xóa được QR.");
      }
      setMessage(`Đã xóa QR ${detail.dynamicId || detail.id}.`);
      setDetailOpen(false);
      loadData();
      loadSummary();
    } catch (err: any) {
      setDetailError(err?.message || "Không xóa được QR.");
    } finally {
      setActionLoading("");
    }
  }

  async function restoreDetail() {
    if (!detail) return;
    setActionLoading("restore");
    setDetailError("");
    try {
      const res: any = await fetch(endpoints.URL_SCAN_BULK, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({ ids: [detail.id], action: "restore" }),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không khôi phục được QR.");
      }
      setMessage(`Đã khôi phục QR ${detail.dynamicId || detail.id}.`);
      setDetailOpen(false);
      loadData();
      loadSummary();
    } catch (err: any) {
      setDetailError(err?.message || "Không khôi phục được QR.");
    } finally {
      setActionLoading("");
    }
  }

  useEffect(() => {
    loadSummary();
    loadData(1);
    // Tải lần đầu; lọc áp dụng qua nút Lọc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const destinationUrl = detail?.destinationUrl || detail?.urlScan?.scannedUrl || "";

  return (
    <div className={styles.ContentWrapper}>
      <div className={styles.Header}>
        <div>
          <h1 className={styles.Title}>QR nghi ngờ</h1>
          <p className={styles.Subtitle}>
            Hệ thống quét URL đích và gắn cờ QR nghi ngờ — không tự chặn. Admin kiểm tra bằng công cụ bên dưới rồi quyết định duyệt / chặn thủ công.
          </p>
        </div>
        {loading && <CircularProgress size={28} />}
      </div>

      <div className={styles.KpiGrid}>
        {KPI_KEYS.map((key) => (
          <div
            key={key}
            className={`${styles.KpiCard} ${styles.KpiClickable} ${key === "reported" ? styles.KpiReported : ""}`}
            onClick={() => {
              setStatus(key);
              setPage(1);
              loadData(1);
            }}
          >
            <div className={styles.KpiLabel}>{key === "reported" ? "🚩 Bị báo cáo" : STATUS_LABELS[key]}</div>
            <div className={styles.KpiValue}>{formatNumber(summary?.counts?.[key])}</div>
            {key === "suspicious" && (
              <div className={styles.KpiMeta}>7 ngày qua: {formatNumber(summary?.recentSuspicious7d)}</div>
            )}
            {key === "reported" && (
              <div className={styles.KpiMeta}>Người dùng chủ động báo cáo</div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.Filters}>
        <div className={styles.InputGroup}>
          <label>Trạng thái</label>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.InputGroup}>
          <label>Tìm kiếm (Dynamic ID / User ID / domain)</label>
          <input value={q} placeholder="R5GIZZFX hoặc evil.com" onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className={styles.InputGroup}>
          <label>Điểm tối thiểu</label>
          <input value={minScore} placeholder="40" onChange={(e) => setMinScore(e.target.value)} />
        </div>
        <div className={styles.FilterActions}>
          <button className={styles.PrimaryButton} onClick={() => { setPage(1); loadData(1); }}>Lọc</button>
          <button
            className={styles.SecondaryButton}
            onClick={() => {
              setQ("");
              setMinScore("");
              setStatus("suspicious");
              setPage(1);
            }}
          >
            Xóa lọc
          </button>
        </div>
      </div>

      {error && <div className={styles.Error}>{error}</div>}
      {message && <div className={styles.Success}>{message}</div>}

      <div className={styles.Panel}>
        <h2 className={styles.PanelTitle}>Danh sách QR ({formatNumber(meta.total)})</h2>
        <p className={styles.PanelHint}>Chọn ô đầu hàng để xử lý hàng loạt; nhấp vào hàng để mở chi tiết.</p>

        {selectedIds.size > 0 && (
          <div className={styles.BulkBar}>
            <span className={styles.BulkCount}>Đã chọn {selectedIds.size}</span>
            {status === "deleted" ? (
              <button className={styles.SuccessButton} disabled={!!bulkLoading} onClick={() => runBulk("restore")}>
                {bulkLoading === "restore" ? "Đang khôi phục…" : "↩ Khôi phục"}
              </button>
            ) : (
              <>
                <button className={styles.SuccessButton} disabled={!!bulkLoading} onClick={() => runBulk("approve")}>
                  {bulkLoading === "approve" ? "Đang duyệt…" : "✓ Duyệt an toàn"}
                </button>
                <button className={styles.DangerButton} disabled={!!bulkLoading} onClick={() => runBulk("block")}>
                  {bulkLoading === "block" ? "Đang chặn…" : "⛔ Chặn"}
                </button>
                <button className={styles.SecondaryButton} disabled={!!bulkLoading} onClick={() => runBulk("unblock")}>
                  {bulkLoading === "unblock" ? "Đang gỡ…" : "Gỡ chặn"}
                </button>
                <button className={styles.SecondaryButton} disabled={!!bulkLoading} onClick={() => runBulk("flag")}>
                  Gắn cờ
                </button>
                <button className={styles.DangerButton} disabled={!!bulkLoading} onClick={() => runBulk("delete")}>
                  {bulkLoading === "delete" ? "Đang xóa…" : "🗑 Xóa"}
                </button>
              </>
            )}
            <button className={styles.SecondaryButton} disabled={!!bulkLoading} onClick={() => setSelectedIds(new Set())}>
              Bỏ chọn
            </button>
          </div>
        )}

        <div className={styles.TableWrap}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={rows.length > 0 && rows.every((r) => selectedIds.has(r.id))}
                    indeterminate={rows.some((r) => selectedIds.has(r.id)) && !rows.every((r) => selectedIds.has(r.id))}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>Dynamic ID</TableCell>
                <TableCell>URL đích</TableCell>
                <TableCell align="right">🚩 Báo cáo</TableCell>
                <TableCell align="right">Điểm</TableCell>
                <TableCell>Lý do</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell align="right">Lượt scan</TableCell>
                <TableCell>Quét lúc</TableCell>
                <TableCell>Trạng thái</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow hover key={row.id} className={styles.ClickableRow} selected={selectedIds.has(row.id)} onClick={() => openDetail(row)}>
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {row.dynamicId ? (
                      <a
                        className={styles.CodeLink}
                        href={publicQrCodeUrl(row.dynamicId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.dynamicId}
                      </a>
                    ) : (
                      <span className={styles.Muted}>{row.id} (tĩnh)</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={styles.UrlCell} title={row.urlScan?.scannedUrl || ""}>
                      {row.urlScan?.scannedUrl || "-"}
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    {row.report?.count ? (
                      <span className={styles.ReportBadge} title={`Báo cáo gần nhất: ${formatDateTime(row.report?.lastReportedAt)}`}>
                        {formatNumber(row.report.count)}
                      </span>
                    ) : (
                      <span className={styles.Muted}>0</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <span className={scoreClass(row.urlScan?.score)}>{row.urlScan?.score ?? "-"}</span>
                  </TableCell>
                  <TableCell>
                    {(row.urlScan?.reasons || []).slice(0, 3).map((reason) => (
                      <span key={reason} className={styles.ReasonChip}>{reason}</span>
                    ))}
                    {(row.urlScan?.reasons || []).length > 3 && (
                      <span className={styles.Muted}>+{(row.urlScan?.reasons || []).length - 3}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={styles.UrlCell} title={row.userId}>{row.userId || "-"}</div>
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.scanCounter)}</TableCell>
                  <TableCell>{formatDateTime(row.urlScan?.scannedAt)}</TableCell>
                  <TableCell>
                    {row.active === false && (
                      <span className={`${styles.StatusBadge} ${styles.StatusDeleted}`} style={{ marginRight: 4 }}>Đã xóa</span>
                    )}
                    <span className={statusBadgeClass(row.urlScanStatus)}>
                      {STATUS_LABELS[row.urlScanStatus || "unscanned"] || row.urlScanStatus}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} className={styles.Muted}>Không có QR nào ở trạng thái này.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className={styles.Pagination}>
          <span>Trang {page} / {meta.pages}</span>
          <button
            className={styles.SecondaryButton}
            disabled={page <= 1 || loading}
            onClick={() => { const p = page - 1; setPage(p); loadData(p); }}
          >
            Trước
          </button>
          <button
            className={styles.SecondaryButton}
            disabled={page >= meta.pages || loading}
            onClick={() => { const p = page + 1; setPage(p); loadData(p); }}
          >
            Sau
          </button>
        </div>
      </div>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: styles.DetailDialogPaper }}
      >
        <DialogTitle className={styles.DetailDialogTitle}>
          <span>
            Kiểm tra QR{" "}
            {detail?.dynamicId && (
              <a
                className={styles.CodeLink}
                href={publicQrCodeUrl(detail.dynamicId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {detail.dynamicId}
              </a>
            )}{" "}
            {detail?.urlScanStatus && (
              <span className={statusBadgeClass(detail.urlScanStatus)}>
                {STATUS_LABELS[detail.urlScanStatus] || detail.urlScanStatus}
              </span>
            )}
          </span>
          <IconButton aria-label="đóng" onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={styles.DetailDialogBody}>
          {detailLoading && (
            <div className={styles.DetailModalLoading}>
              <CircularProgress size={32} />
              <span className={styles.Muted}>Đang tải chi tiết…</span>
            </div>
          )}
          {!detailLoading && detailError && <div className={styles.DetailModalError}>{detailError}</div>}
          {!detailLoading && detail && (
            <>
              <div className={styles.DetailGrid}>
                <div className={styles.DetailItem}><strong>URL đích</strong>{destinationUrl || "-"}</div>
                <div className={styles.DetailItem}>
                  <strong>Điểm nghi ngờ</strong>
                  <span className={scoreClass(detail.urlScan?.score)}>{detail.urlScan?.score ?? "-"}</span>
                  {" "}({detail.urlScan?.engine || "-"})
                </div>
                <div className={styles.DetailItem}><strong>Owner user</strong>{detail.userId || "-"}</div>
                <div className={styles.DetailItem}><strong>Owner email</strong>{detail.ownerEmail || "-"}</div>
                <div className={styles.DetailItem}><strong>Ownership</strong>{detail.ownershipStatus || "-"}</div>
                <div className={styles.DetailItem}><strong>Content type</strong>{detail.contentType || "-"}</div>
                <div className={styles.DetailItem}><strong>Lượt scan</strong>{formatNumber(detail.scanCounter)}</div>
                <div className={styles.DetailItem}><strong>Tạo lúc</strong>{formatDateTime(detail.createdAt)}</div>
                <div className={styles.DetailItem}><strong>Quét lúc</strong>{formatDateTime(detail.urlScan?.scannedAt)} ({detail.urlScan?.trigger || "-"})</div>
                <div className={styles.DetailItem}>
                  <strong>Review gần nhất</strong>
                  {detail.urlScan?.reviewedBy
                    ? `${detail.urlScan.reviewedBy} — ${formatDateTime(detail.urlScan?.reviewedAt)}${detail.urlScan?.reviewNote ? ` — "${detail.urlScan.reviewNote}"` : ""}`
                    : "Chưa có"}
                </div>
              </div>

              <div className={styles.DetailItem} style={{ marginTop: 8 }}>
                <strong>Lý do gắn cờ</strong>
                {(detail.urlScan?.reasons || []).length > 0
                  ? (detail.urlScan?.reasons || []).map((reason) => (
                    <span key={reason} className={styles.ReasonChip}>{reason}</span>
                  ))
                  : <span className={styles.Muted}>Không có</span>}
              </div>

              {(detail.report?.count || 0) > 0 && (
                <>
                  <h3 className={styles.DetailModalSubtitle}>
                    🚩 Báo cáo từ người dùng ({formatNumber(detail.report?.count)}) — gần nhất {formatDateTime(detail.report?.lastReportedAt)}
                  </h3>
                  <div className={styles.DetailModalTableWrap}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Thời gian</TableCell>
                          <TableCell>Lý do</TableCell>
                          <TableCell>Mô tả</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...(detail.report?.reasons || [])].reverse().map((r, index) => (
                          <TableRow key={`${r.at}-${index}`}>
                            <TableCell>{formatDateTime(r.at)}</TableCell>
                            <TableCell>{REPORT_REASON_LABELS[r.reason] || r.reason}</TableCell>
                            <TableCell>{r.note || <span className={styles.Muted}>—</span>}</TableCell>
                          </TableRow>
                        ))}
                        {(detail.report?.reasons || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className={styles.Muted}>Không có chi tiết lý do.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              <h3 className={styles.DetailModalSubtitle}>Công cụ kiểm tra</h3>
              <div className={styles.ToolBar}>
                <button className={styles.ToolButton} disabled={actionLoading === "rescan"} onClick={rescan}>
                  {actionLoading === "rescan" ? "Đang quét…" : "🔄 Quét lại"}
                </button>
                <button className={styles.ToolButton} disabled={chainLoading || !destinationUrl} onClick={checkChain}>
                  {chainLoading ? "Đang kiểm tra…" : "🔗 Kiểm tra chuỗi redirect"}
                </button>
                {destinationUrl && (
                  <>
                    <button
                      className={styles.ToolButton}
                      onClick={() => navigator.clipboard?.writeText(destinationUrl)}
                    >
                      📋 Copy URL
                    </button>
                    <a
                      href={`https://transparencyreport.google.com/safe-browsing/search?url=${encodeURIComponent(destinationUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <button className={styles.ToolButton}>Google Safe Browsing ↗</button>
                    </a>
                    <a
                      href={`https://www.virustotal.com/gui/search/${encodeURIComponent(encodeURIComponent(destinationUrl))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <button className={styles.ToolButton}>VirusTotal ↗</button>
                    </a>
                    <a
                      href={`https://urlscan.io/search/#${encodeURIComponent(destinationUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <button className={styles.ToolButton}>urlscan.io ↗</button>
                    </a>
                  </>
                )}
              </div>

              {chain && (
                <>
                  <h3 className={styles.DetailModalSubtitle}>
                    Chuỗi redirect ({chain.hops} bước) → URL cuối:{" "}
                    <span className={styles.UrlCell} style={{ display: "inline", maxWidth: "none" }}>{chain.finalUrl}</span>
                    {chain.finalAnalysis && (
                      <span className={scoreClass(chain.finalAnalysis.score)}> — điểm {chain.finalAnalysis.score}</span>
                    )}
                  </h3>
                  <div className={styles.DetailModalTableWrap}>
                    {chain.chain.map((hop, index) => (
                      <div key={`${hop.url}-${index}`} className={styles.ChainHop}>
                        <div>
                          #{index + 1} {hop.url}
                          {typeof hop.status === "number" && <span className={styles.Muted}> — HTTP {hop.status}</span>}
                          {hop.metaRefresh && <span className={styles.Muted}> — meta refresh → {hop.metaRefresh}</span>}
                        </div>
                        {hop.error && <div className={styles.ChainError}>⚠️ {hop.error}</div>}
                        {hop.analysis && (hop.analysis.reasons || []).length > 0 && (
                          <div>
                            {hop.analysis.reasons.map((reason) => (
                              <span key={reason} className={styles.ReasonChip}>{reason}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h3 className={styles.DetailModalSubtitle}>Xử lý thủ công</h3>
              <p className={styles.Muted} style={{ fontSize: 12, margin: "0 0 4px" }}>
                Chặn: người quét thấy trang cảnh báo thay vì được chuyển tới URL đích (gỡ được bằng Mở chặn).
                Duyệt an toàn: hệ thống không gắn cờ lại trừ khi URL đích thay đổi.
              </p>
              <input
                className={styles.NoteInput}
                value={reviewNote}
                placeholder="Ghi chú xử lý (tùy chọn) — vd: đã kiểm tra, trang phishing giả ngân hàng"
                onChange={(e) => setReviewNote(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className={styles.DetailDialogActions}>
          {detail && !detailLoading && detail.active === false && (
            <button
              type="button"
              className={styles.SuccessButton}
              disabled={!!actionLoading}
              onClick={restoreDetail}
            >
              {actionLoading === "restore" ? "Đang khôi phục…" : "↩ Khôi phục QR"}
            </button>
          )}
          {detail && !detailLoading && detail.active !== false && (
            <>
              {detail.urlScanStatus !== "blocked" && (
                <button
                  type="button"
                  className={styles.DangerButton}
                  disabled={!!actionLoading}
                  onClick={() => review("block")}
                >
                  {actionLoading === "block" ? "Đang chặn…" : "⛔ Chặn QR"}
                </button>
              )}
              {detail.urlScanStatus === "blocked" && (
                <button
                  type="button"
                  className={styles.SuccessButton}
                  disabled={!!actionLoading}
                  onClick={() => review("unblock")}
                >
                  {actionLoading === "unblock" ? "Đang mở…" : "Mở chặn"}
                </button>
              )}
              {detail.urlScanStatus !== "approved" && detail.urlScanStatus !== "blocked" && (
                <button
                  type="button"
                  className={styles.SuccessButton}
                  disabled={!!actionLoading}
                  onClick={() => review("approve")}
                >
                  {actionLoading === "approve" ? "Đang duyệt…" : "✓ Duyệt an toàn"}
                </button>
              )}
              {detail.urlScanStatus !== "suspicious" && detail.urlScanStatus !== "blocked" && (
                <button
                  type="button"
                  className={styles.SecondaryButton}
                  disabled={!!actionLoading}
                  onClick={() => review("flag")}
                >
                  Gắn cờ nghi ngờ
                </button>
              )}
              <button
                type="button"
                className={styles.DangerButton}
                disabled={!!actionLoading}
                onClick={deleteDetail}
              >
                {actionLoading === "delete" ? "Đang xóa…" : "🗑 Xóa QR"}
              </button>
            </>
          )}
          <button type="button" className={styles.PrimaryButton} onClick={() => setDetailOpen(false)}>
            Đóng
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UrlScanReview;
